/**
 * US-04 / US-05 / US-13: Read-only client hints — must match `functions/lib/enhancementEntitlement.js`.
 */

const LEGACY_ADMIN_MIGRATION_BILLING_PLAN_ID = 'legacy_admin_migration';

/** @param {unknown} ts Firestore Timestamp-like or Date */
function expiryToMillis(ts) {
  if (ts == null) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  return null;
}

export function hasActiveOverride(data) {
  if (!data) return false;
  const kind = data.overrideKind;
  if (kind !== 'comp') return false;
  const exp = data.overrideExpiresAt;
  if (exp == null) return true;
  const ms = expiryToMillis(exp);
  if (ms == null) return true;
  return ms > Date.now();
}

export function hasActiveBillingSubscription(data) {
  if (!data) return false;
  const status = data.billingStatus;
  if (!status || status === 'none') return false;
  const s = String(status).toLowerCase();
  return ['active', 'trialing', 'past_due'].includes(s);
}

export function hasPaidEntitlement(data) {
  if (!data) return false;
  if (data.billingPlanId === LEGACY_ADMIN_MIGRATION_BILLING_PLAN_ID) {
    return true;
  }
  if (!hasActiveBillingSubscription(data)) {
    return false;
  }
  const n = Number(data.paidCreditsRemaining);
  return Number.isFinite(n) && n > 0;
}

export function hasFreePassCredits(data) {
  if (!data || data.freePassCreditsRemaining == null) return false;
  const n = Number(data.freePassCreditsRemaining);
  return Number.isFinite(n) && n > 0;
}

/**
 * @param {{ summary: object | null | undefined }} params
 * @returns {{ allowed: boolean, reason: string }}
 */
export function evaluateEnhancementEntitlementHint({ summary }) {
  if (summary == null) {
    return { allowed: false, reason: 'missing_summary' };
  }
  if (hasActiveOverride(summary)) {
    return { allowed: true, reason: 'override' };
  }
  if (hasPaidEntitlement(summary)) {
    return { allowed: true, reason: 'paid' };
  }
  if (hasFreePassCredits(summary)) {
    return { allowed: true, reason: 'free_pass' };
  }
  if (hasActiveBillingSubscription(summary)) {
    return { allowed: false, reason: 'paid_credits_exhausted' };
  }
  return { allowed: false, reason: 'denied' };
}

/** @param {unknown} ts */
function formatPeriodEndHint(ts) {
  const ms = expiryToMillis(ts);
  if (ms == null) return null;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

export function shouldShowSubscribeCta(summary) {
  if (!summary) return true;
  if (hasActiveOverride(summary)) return false;
  if (hasActiveBillingSubscription(summary)) return false;
  if (summary.billingPlanId === LEGACY_ADMIN_MIGRATION_BILLING_PLAN_ID) return false;
  return true;
}

export function shouldShowManageBillingCta(summary) {
  return hasActiveBillingSubscription(summary);
}

/**
 * Short message for Path Selector footer (US-04 / US-09 / US-13).
 */
export function getEntitlementFooterHint({
  loading,
  summary,
  allowed,
  snapshotError,
}) {
  if (snapshotError) {
    return 'Could not load enhancement status. Check your connection and try again.';
  }
  if (loading) {
    return 'Checking enhancement access…';
  }
  if (allowed && summary && hasPaidEntitlement(summary)) {
    const n = Number(summary.paidCreditsRemaining);
    if (Number.isFinite(n) && n > 0) {
      return n === 1
        ? 'You have 1 paid enhancement credit remaining this billing period.'
        : `You have ${n} paid enhancement credits remaining this billing period.`;
    }
  }
  if (allowed && summary && hasFreePassCredits(summary)) {
    const n = Number(summary.freePassCreditsRemaining);
    if (Number.isFinite(n) && n > 0) {
      return n === 1
        ? 'You have 1 complimentary AI enhancement available.'
        : `You have ${n} complimentary AI enhancements available.`;
    }
  }
  if (allowed) {
    return null;
  }
  if (summary && hasActiveBillingSubscription(summary)) {
    const renew = formatPeriodEndHint(summary.billingCurrentPeriodEnd);
    return renew
      ? `Your paid credits for this period are used up. Credits renew around ${renew}.`
      : 'Your paid credits for this period are used up. Credits renew when your subscription renews.';
  }
  if (summary && !hasFreePassCredits(summary) && summary.freePassConsumedAt != null) {
    return 'Your complimentary AI enhancement has been used. Subscribe for $10/month (100 credits/month) or see Support.';
  }
  if (summary == null) {
    return 'Enhancement access could not be loaded. Try refreshing the page or contact support.';
  }
  return "AI enhancement isn't available for your account right now. Subscribe for $10/month or see Support.";
}
